import { Widget } from "@foretagsplatsen/widgetjs";

/**
 * Widget to select an index. It shows a plus button and a minus button.
 * It is basically a more visually appealing number input.
 * The value is stored internally, and exposed to the outer world via
 * its callback method.
 *
 * @param {number} [max] - Maximum value allowed for the index
 * @param {number} [min] - Minimum value allowed for the index. When provided, serves as the base value for `index`
 * @param {number} [index] - Base value for the index
 * @param {function} [callback] - Callback to trigger when then index is increased/decreased
 * @param {renderer} [indexRenderer] - Function to tweak the rendering of the
 * current index which is passed as argument.
 */
export default class IndexPicker extends Widget {
	constructor({
		max,
		min,
		index = min ?? 0,
		callback = () => {},
		indexRenderer = (label) => label,
	}) {
		super();

		this._min = min;
		this._max = max;
		this._index = index;
		this._callback = callback;
		this._indexRenderer = indexRenderer;
	}

	renderContentOn(html) {
		html.div(
			{
				klass: "index-picker",
				"data-test": "index-picker",
			},
			html.div(
				{ klass: "index-picker-btns" },
				this._renderMinusButtonOn.bind(this),
				this._renderLabelOn.bind(this),
				this._renderPlusButtonOn.bind(this),
			),
		);
	}

	_renderMinusButtonOn(html) {
		html.a(
			{
				"data-test": "decrement",
				klass: [
					"btn",
					"btn-default",
					{ disabled: this._index <= this._min },
				],
				click: () => this._setIndex(this._index - 1),
			},
			"-",
		);
	}

	_renderLabelOn(html) {
		html.span(
			{ klass: "index-label", "data-test": "index-label" },
			this._indexRenderer(this._index),
		);
	}

	_renderPlusButtonOn(html) {
		const disabledClass = this._index >= this._max ? " disabled" : "";

		html.a(
			{
				"data-test": "increment",
				klass: ["btn", "btn-default", disabledClass],
				click: () => this._setIndex(this._index + 1),
			},
			"+",
		);
	}

	_setIndex(index) {
		this._index = index;
		this._callback(index);

		this.update();
	}
}
